'use client';

import { useRef, useCallback, useEffect, useState } from 'react';
import { IImageVideoCarousel } from '@/.generated';
import { CarouselWrapper, CarouselSlide } from '@/helpers/Wrappers/CarouselWrapper/CarouselWrapper';
import ImageWrapper from '@/helpers/Wrappers/ImageWrapper/ImageWrapper';
import VimeoPlayer from '@/helpers/Wrappers/VimeoPlayer/VimeoPlayer';
import { getCSLPAttributes } from '@/utils/type-guards';
import { videoCarouselVariants } from './VideoCarousel.styles';

interface VideoCarouselProps extends Required<Pick<IImageVideoCarousel, 'carousel_items'>> {
  carousel_settings?: IImageVideoCarousel['carousel_settings'];
  isContainerBleedCarousel?: boolean;
}

const VideoCarousel = ({
  carousel_items,
  carousel_settings,
  isContainerBleedCarousel = false,
}: VideoCarouselProps) => {
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  // Listen for Vimeo play/pause/ended events scoped to this carousel's iframes
  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (!e.origin.includes('vimeo.com')) return;

      let msg: { event?: string; player_id?: string } | null = null;
      try {
        msg = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
      } catch {
        return;
      }
      if (!msg?.event) return;

      // Ignore messages from Vimeo players outside this carousel
      const iframes = carouselRef.current?.querySelectorAll('iframe');
      const belongsToCarousel = Array.from(iframes ?? []).some(
        (iframe) => iframe.contentWindow === e.source
      );
      if (!belongsToCarousel) return;

      // Proactively subscribe to play/pause/ended on every iframe so we never miss events
      if (msg.event === 'ready') {
        ['play', 'pause', 'ended'].forEach((evt) => {
          (e.source as Window)?.postMessage(
            JSON.stringify({ method: 'addEventListener', value: evt, player_id: msg?.player_id }),
            'https://player.vimeo.com'
          );
        });
      }

      if (msg.event === 'play') {
        setIsVideoPlaying(true);
      }

      if (msg.event === 'pause' || msg.event === 'ended') {
        setIsVideoPlaying(false);
      }
    };

    // Fires the moment the user clicks any iframe in the carousel — earlier than Vimeo's play event,
    // which can lag behind the click while the video buffers
    const handleWindowBlur = () => {
      setTimeout(() => {
        const active = document.activeElement;
        if (active instanceof HTMLIFrameElement && carouselRef.current?.contains(active)) {
          setIsVideoPlaying(true);
        }
      }, 0);
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('blur', handleWindowBlur);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, []);

  const handleSlideChange = useCallback(() => {
    setIsVideoPlaying(false);
    // Pause all videos in the carousel by posting to all iframes
    const iframes = carouselRef.current?.querySelectorAll('iframe');
    iframes?.forEach((iframe) => {
      iframe.contentWindow?.postMessage({ method: 'pause' }, '*');
    });
  }, []);
  const { slideContent, thumbsWrapper, thumbContainer, thumbDetails } = videoCarouselVariants();

  const visibleItems = carousel_items.filter((item) => item.video_id);
  if (visibleItems.length === 0) return null;

  const thumbnails = visibleItems.map((item, index) => (
    <div key={`thumb-${index}`} className={thumbContainer()} data-video-id={item.video_id}>
      {/* No fill/aspect box — ImageWrapper uses the asset's intrinsic dimensions so thumbnails keep their native ratio */}
      <ImageWrapper
        image={{
          image: item.carousel_image,
          rounded_image: false,
          alternate_text: item.image_alt,
        }}
        showFallbackImage
      />
      {item.title && (
        <p className={thumbDetails()} {...getCSLPAttributes(item.$?.title)}>
          {item.title}
        </p>
      )}
    </div>
  ));

  const isSingle = visibleItems.length === 1;

  return (
    <div ref={carouselRef}>
      <CarouselWrapper
        ariaLabel="Video carousel"
        onSlideChange={handleSlideChange}
        isContainerBleedCarousel={isContainerBleedCarousel}
        thumbsClassName={thumbsWrapper()}
        {...carousel_settings}
        showDots={!isSingle}
        showPaginationArrows={!isSingle}
        autoplay={isSingle ? false : carousel_settings?.autoplay}
        pauseAutoplay={isVideoPlaying}
        thumbnails={isSingle ? undefined : thumbnails}
      >
        {visibleItems.map((item, index) => (
          <CarouselSlide
            key={`video-slide-${index}`}
            fade
            slideMeta={{ index, total: visibleItems.length }}
          >
            <div className={slideContent()}>
              <VimeoPlayer
                videoId={item.video_id}
                title={item.title || `Video ${index + 1}`}
                {...getCSLPAttributes(item.$?.video_id)}
              />
            </div>
          </CarouselSlide>
        ))}
      </CarouselWrapper>
    </div>
  );
};

export default VideoCarousel;
